import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Settings2, X } from "lucide-react";
import { getConsent, setConsent, loadGtag } from "@/lib/consent";

/**
 * DSGVO-konformer Consent-Banner mit zwei Kategorien:
 *  - Technisch notwendig (immer aktiv, nicht abwählbar)
 *  - Marketing (Google Ads. Opt-In per Toggle)
 * Google-Ads-Tag wird ausschließlich nach expliziter Zustimmung nachgeladen.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const stored = getConsent();
    if (!stored) {
      const t = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(t);
    }
    if (stored.marketing) loadGtag();
  }, []);

  const acceptAll = () => {
    setConsent({ marketing: true });
    loadGtag();
    setVisible(false);
  };
  const rejectAll = () => {
    setConsent({ marketing: false });
    setVisible(false);
  };
  const saveChoice = () => {
    setConsent({ marketing });
    if (marketing) loadGtag();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-banner"
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[60]"
          role="dialog"
          aria-label="Cookie-Einstellungen"
          aria-modal="false"
        >
          <div className="bg-[#151519] border border-white/10 rounded-2xl px-5 py-4 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs sm:text-sm text-bone/80 leading-relaxed">
                Wir nutzen Cookies, um dir diese Seite auszuspielen und, mit
                deiner Einwilligung. Marketing Reichweite zu messen (Google
                Ads). Du bestimmst, was aktiv ist.{" "}
                <a
                  href="/datenschutz"
                  className="text-signal hover:text-bone transition-colors underline-offset-4 hover:underline"
                >
                  Mehr erfahren
                </a>
                .
              </p>
              <button
                type="button"
                aria-label="Einstellungen anpassen"
                data-testid="cookie-toggle-details"
                onClick={() => setShowDetails((s) => !s)}
                className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full border border-white/15 text-bone/70 hover:border-signal hover:text-signal transition-colors"
              >
                {showDetails ? <X size={13} /> : <Settings2 size={13} />}
              </button>
            </div>

            <AnimatePresence initial={false}>
              {showDetails && (
                <motion.div
                  key="details"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <ul className="mt-4 space-y-3 border-t border-white/10 pt-4">
                    <li className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-xs font-semibold text-bone">
                          Technisch notwendig
                        </div>
                        <div className="text-[11px] text-bone/50 mt-0.5">
                          Für Grundfunktionen der Seite erforderlich.
                        </div>
                      </div>
                      <span className="shrink-0 text-[10px] uppercase tracking-widest text-signal mt-1">
                        Immer an
                      </span>
                    </li>
                    <li className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-xs font-semibold text-bone">
                          Marketing (Google Ads)
                        </div>
                        <div className="text-[11px] text-bone/50 mt-0.5">
                          Misst Anzeigenerfolg und ermöglicht Remarketing.
                        </div>
                      </div>
                      <Toggle
                        checked={marketing}
                        onChange={setMarketing}
                        testid="cookie-toggle-marketing"
                        label="Marketing Cookies erlauben"
                      />
                    </li>
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              {showDetails ? (
                <>
                  <button
                    data-testid="cookie-save"
                    onClick={saveChoice}
                    className="flex-1 min-w-[140px] bg-signal text-ink text-xs sm:text-sm font-bold tracking-wide uppercase px-5 py-3 rounded-full hover:bg-signal/85 transition-colors duration-300 shadow-[0_0_20px_rgba(63,211,219,0.35)]"
                  >
                    Auswahl speichern
                  </button>
                  <button
                    data-testid="cookie-decline"
                    onClick={rejectAll}
                    className="text-xs tracking-wide text-bone/45 hover:text-bone/80 underline underline-offset-4 px-2 py-2 transition-colors duration-300"
                  >
                    Nur notwendige
                  </button>
                </>
              ) : (
                <>
                  <button
                    data-testid="cookie-accept"
                    onClick={acceptAll}
                    className="flex-1 min-w-[200px] bg-signal text-ink text-sm font-bold tracking-wide uppercase px-6 py-3.5 rounded-full hover:bg-signal/90 transition-all duration-300 shadow-[0_0_24px_rgba(63,211,219,0.4)] hover:shadow-[0_0_32px_rgba(63,211,219,0.6)]"
                  >
                    Alle akzeptieren
                  </button>
                  <button
                    data-testid="cookie-decline"
                    onClick={rejectAll}
                    className="text-xs tracking-wide text-bone/45 hover:text-bone/80 underline underline-offset-4 px-2 py-2 transition-colors duration-300"
                  >
                    Nur notwendige
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Toggle({ checked, onChange, testid, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className={`shrink-0 mt-0.5 relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${
        checked ? "bg-signal" : "bg-white/20"
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-300 ${
          checked ? "translate-x-4.5" : "translate-x-1"
        }`}
        style={{ transform: checked ? "translateX(18px)" : "translateX(4px)" }}
      />
    </button>
  );
}
